import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Share2,
  Maximize2,
  Filter,
  Calendar } from
'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";
import { RouteReplayMap } from '../../components/trails/RouteReplayMap';
import { TrailEventTimeline } from '../../components/trails/TrailEventTimeline';
import { StopsList } from '../../components/trails/StopsList';
import { ShipmentList } from '../../components/trails/ShipmentCard';

// Mock data for route replay
const mockShipmentsForReplay = [
{ id: 'SHP-001', trackingNumber: 'TRK-001', status: 'in_transit', origin: 'São Paulo', destination: 'Rio de Janeiro' },
{ id: 'SHP-002', trackingNumber: 'TRK-002', status: 'delivered', origin: 'Curitiba', destination: 'Florianópolis' }];


const mockGPSTrail = {
  breadcrumbs: [
  { lat: -23.5505, lng: -46.6333, timestamp: new Date().toISOString(), speed: 45 },
  { lat: -23.5510, lng: -46.6340, timestamp: new Date().toISOString(), speed: 50 }]

};

const mockAuditTrail = [
{ id: 1, action: 'status_change', timestamp: new Date().toISOString(), details: 'Status changed to In Transit' }];


export const RouteReplay = () => {const { t } = useTranslation("ezparcel");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shipmentIdParam = searchParams.get('shipment');

  // State
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showSidebar, setShowSidebar] = useState(true);

  const playIntervalRef = useRef(null);

  // Initialize selected shipment from URL param or first available
  useEffect(() => {
    if (shipmentIdParam) {
      const shipment = mockShipmentsForReplay.find((s) => s.id === shipmentIdParam);
      if (shipment) {
        setSelectedShipment(shipment);
      }
    } else if (mockShipmentsForReplay.length > 0) {
      setSelectedShipment(mockShipmentsForReplay[0]);
    }
  }, [shipmentIdParam]);

  // Get GPS data for selected shipment (using mock data)
  const gpsData = mockGPSTrail;

  // Playback logic
  useEffect(() => {
    if (isPlaying && gpsData?.breadcrumbs) {
      playIntervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= gpsData.breadcrumbs.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000 / playbackSpeed);
    }

    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, gpsData?.breadcrumbs?.length]);

  // Handlers
  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleSeek = useCallback((index) => {
    setCurrentIndex(index);
    setIsPlaying(false);
  }, []);

  const handleReset = useCallback(() => {
    setCurrentIndex(0);
    setIsPlaying(false);
  }, []);

  const handleShipmentSelect = (shipment) => {
    setSelectedShipment(shipment);
    setCurrentIndex(0);
    setIsPlaying(false);
    navigate(`/ezparcel/trails/replay?shipment=${shipment.id}`, { replace: true });
  };

  const handleReplay = (shipment) => {
    setSelectedShipment(shipment);
    setCurrentIndex(0);
    setIsPlaying(true);
    navigate(`/ezparcel/trails/replay?shipment=${shipment.id}`, { replace: true });
  };

  // Calculate current stop based on breadcrumb position
  const getCurrentStopIndex = () => {
    if (!gpsData?.breadcrumbs) return 0;
    const completedDepartures = gpsData.breadcrumbs.
    slice(0, currentIndex + 1).
    filter((b) => b.event === 'STOP_DEPARTURE').
    length;
    return completedDepartures;
  };

  // Get timeline events (filtered breadcrumbs with events)
  const timelineEvents = gpsData?.breadcrumbs?.filter((b) => b.event) || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="route-replay-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/ezparcel/trails')}
              data-testid="back-button">
              
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t("routeReplay", "Route Replay")}</h1>
              <p className="text-sm text-gray-500">
                {selectedShipment?.name || 'Select a shipment'} · {selectedShipment?.date}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Playback speed */}
            <Select value={String(playbackSpeed)} onValueChange={(v) => setPlaybackSpeed(Number(v))}>
              <SelectTrigger className="w-[100px]" data-testid="speed-selector">
                <SelectValue placeholder="Speed" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.5">{t("05x", "0.5x")}</SelectItem>
                <SelectItem value="1">{t("1x", "1x")}</SelectItem>
                <SelectItem value="2">{t("2x", "2x")}</SelectItem>
                <SelectItem value="4">{t("4x", "4x")}</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" size="icon" onClick={() => setShowSidebar(!showSidebar)}>
              <Maximize2 className="h-4 w-4" />
            </Button>
            
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />{t("export", "Export")}

            </Button>
            
            <Button variant="outline" className="gap-2">
              <Share2 className="h-4 w-4" />{t("share", "Share")}

            </Button>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex">
        {/* Sidebar - Shipment list */}
        {showSidebar &&
        <div className="w-80 border-r border-gray-200 bg-white min-h-[calc(100vh-73px)] overflow-y-auto">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 mb-1">{t("availableShipments", "Available Shipments")}</h2>
              <p className="text-xs text-gray-500">{t("selectAShipment", "Select a shipment to replay its route")}</p>
            </div>
            <div className="p-4">
              <ShipmentList
              shipments={mockShipmentsForReplay}
              selectedId={selectedShipment?.id}
              onSelect={handleShipmentSelect}
              onReplay={handleReplay}
              compact />
            
            </div>
          </div>
        }
        
        {/* Main replay area */}
        <div className="flex-1 p-6">
          {selectedShipment ?
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Map - spans 2 columns on xl */}
              <div className="xl:col-span-2">
                <RouteReplayMap
                gpsData={gpsData}
                currentIndex={currentIndex}
                isPlaying={isPlaying}
                onPlayPause={handlePlayPause}
                onSeek={handleSeek}
                onReset={handleReset} />
              
              </div>
              
              {/* Right side panel */}
              <div className="space-y-6">
                {/* Stops list */}
                <StopsList
                stops={gpsData?.stops || []}
                currentStopIndex={getCurrentStopIndex()} />
              
                
                {/* Timeline */}
                <TrailEventTimeline
                events={timelineEvents}
                currentIndex={timelineEvents.findIndex((e) => {
                  const eventIdx = gpsData?.breadcrumbs?.indexOf(e);
                  return eventIdx === currentIndex;
                })}
                onEventClick={(event) => {
                  const idx = gpsData?.breadcrumbs?.indexOf(event);
                  if (idx >= 0) handleSeek(idx);
                }}
                maxHeight={350}
                showFilters />
              
              </div>
            </div> :

          <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t("noShipmentSelected", "No Shipment Selected")}</h3>
              <p className="text-sm text-gray-500">{t("selectAShipment", "Select a shipment from the sidebar to start the route replay")}</p>
            </div>
          }
        </div>
      </div>
    </div>);

};